// src/components/Layout.js
import React, { useEffect, useState } from 'react';
import { Box, CssBaseline, AppBar, Toolbar, Typography, Button, Fab, Tooltip, IconButton } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Sidebar from './Sidebar';
import { Outlet, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { collection, onSnapshot, query, where, orderBy as fbOrderBy } from 'firebase/firestore';
import ChatbotDialog from './ChatbotDialog';

function Layout() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { 
    setImports, setExports, setImportCount, 
    setExportCount, setProcessingExportCount, setCanRecoveryCount,
    setProducts, setSuppliers, setCustomers, setDrivers,
    setAssistantDrivers, setVehicles, setWarehouses
  } = useAppContext();
  const [chatbotOpen, setChatbotOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    // Tải tất cả dữ liệu danh mục dùng chung
    const collectionsToWatch = {
      products: setProducts,
      suppliers: setSuppliers,
      customers: setCustomers,
      drivers: setDrivers,
      assistantDrivers: setAssistantDrivers,
      vehicles: setVehicles,
      warehouses: setWarehouses,
    };

    const unsubs = Object.entries(collectionsToWatch).map(([name, setter]) => 
      onSnapshot(query(collection(db, name)), snapshot => 
        setter(snapshot.docs.map(d => ({id: d.id, ...d.data()})))
      )
    );

    // Listener cho Hàng Nhập
    const qImports = query(collection(db, "imports"), fbOrderBy("importDate", "desc"));
    const unsubImports = onSnapshot(qImports, (snapshot) => {
        const importList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setImports(importList);
        setImportCount(importList.length);
    });

    // Listener cho Hàng Xuất
    let qExports;
    const isAdminOrManager = currentUser.roleNames?.some(name => name.toLowerCase() === 'admin') || currentUser.permissions?.viewAllData;
    if (isAdminOrManager) {
      qExports = query(collection(db, "exports"), fbOrderBy("exportDate", "desc"));
    } else {
      qExports = query(collection(db, "exports"), where("createdBy", "==", currentUser.email), fbOrderBy("exportDate", "desc"));
    }
    const unsubExports = onSnapshot(qExports, (snapshot) => {
        const exportList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setExports(exportList);
        setExportCount(exportList.length);
        const processingCount = exportList.filter(o => (o.status || 'processing') === 'processing').length;
        setProcessingExportCount(processingCount);
    });

    // Listener cho Vỏ Can
    const qRecoveries = query(collection(db, "recoveries"));
    const unsubRecoveries = onSnapshot(qRecoveries, (snapshot) => {
        const recoveryList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const relevantCount = recoveryList.filter(item => {
            const hasValidItems = Array.isArray(item.recoveredItems) && item.recoveredItems.length > 0 && item.recoveredItems.some(prod => prod.productName);
            const isNotProcessing = (item.statusUpdatedTo || 'processing') !== 'processing';
            return hasValidItems && isNotProcessing;
        }).length;
        
        setCanRecoveryCount(relevantCount);
    });
    
    unsubs.push(unsubImports, unsubExports, unsubRecoveries);

    return () => {
        unsubs.forEach(unsub => unsub());
    };
  }, [currentUser, setImports, setExports, setImportCount, setExportCount, setProcessingExportCount, setCanRecoveryCount, setProducts, setSuppliers, setCustomers, setDrivers, setAssistantDrivers, setVehicles, setWarehouses]);


  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Lỗi khi đăng xuất:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Hệ Thống Quản Lý Kho
          </Typography>
          
          {currentUser && (
            <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '400px', mr: 2 }}>
              <Typography variant="body2" sx={{ display: 'inline-block', animation: 'marquee 15s linear', pl: '100%', '@keyframes marquee': {'0%': { transform: 'translateX(0%)' }, '100%': { transform: 'translateX(-100%)' } } }}>
                Xin chào, {currentUser.fullName} ({currentUser.department || 'N/A'})
              </Typography>
            </Box>
          )}

          <Tooltip title="Trợ lý AI">
            <IconButton color="inherit" onClick={() => setChatbotOpen(true)}>
                <SmartToyIcon />
            </IconButton>
          </Tooltip>

          <Button color="inherit" onClick={handleLogout} sx={{ ml: 1 }}>Đăng Xuất</Button>
        </Toolbar>
      </AppBar>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
        <Toolbar />
        <Outlet /> 
      </Box>
      <ChatbotDialog open={chatbotOpen} onClose={() => setChatbotOpen(false)} />
    </Box>
  );
}

export default Layout;
